/* eslint-disable @typescript-eslint/no-use-before-define */ // disabled to use hoisting
import { ChangeEvent, FC, FormEvent, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import pick from 'lodash/pick';
import { useImmer } from 'use-immer';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import { HELP_ROUTE } from 'components/help/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import ELEMENT from 'common/element';
import { Permission } from 'common/graphqlTypes';

import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { accountDataState, hasPermissionState } from 'components/account/account.state';
import ScrubbingRuleForm from 'components/logProcessing/ScrubbingRuleForm';
import ScrubbingRulesList from 'components/logProcessing/ScrubbingRulesList/ScrubbingRulesList';
import AboutScrubbing from 'components/logProcessing/AboutScrubbing';

import { ScrubbingRule, ScrubbingRuleFormData, ScrubbingRuleFormVariant } from 'components/logProcessing/types';
import { ADD_RULE, DELETE_RULE, GET_RULES, MOVE_RULE, UPDATE_RULE } from 'components/logProcessing/queries';

import './LogProcessingPage.scss';

const scrubbingRuleFormInitialValue: ScrubbingRuleFormData = {
  variant: ScrubbingRuleFormVariant.ADD,
  values: {
    name: '',
    logFilter: '',
    matchText: '',
    replaceWith: '',
  },
};

/**
 * After the Scalyr Agent hands off each log entry to the cloud, but before you can search for it, that log entry has to be processed.
 * The first processing step is Data Scrubbing, where you can redact passwords or other sensitive information by rewriting or removing certain portions of a log entry.
 * Please note that this step occurs before parsing, so you are operating on a raw log line.
 * @see https://scalyr.com/help/log-processing
 */
const LogProcessingPage: FC = () => {
  const { data: { scrubbingRules } = { scrubbingRules: [] }, updateQuery } = useQuery<{
    scrubbingRules: ScrubbingRule[];
  }>(GET_RULES);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [formValidated, setFormValidated] = useState(false);
  const [scrubbingRuleForm, setScrubbingRuleForm] = useImmer<ScrubbingRuleFormData>(scrubbingRuleFormInitialValue);
  const [filterFade, setFilterFade] = useState(false);

  const scrubbingfilterRules = (
    <>
      <span>Because scrubbing occurs before parsing, you are limited to filtering by:</span>
      <ul className="LogProcessingPage__AboutScrubbing__FilteringRules">
        <li>The standard log &quot;message&quot; field</li>
        <li>Server attributes -- e.g., from the Scalyr agent</li>
        <li>Structured fields sent to Scalyr via the API</li>
      </ul>
    </>
  );

  const [addRuleMutation] = useMutation(ADD_RULE, {
    onCompleted: () => {
      handleSuccessAlert('Filter added');
      handleFormReset();
    },
    onError: (appolloError: ApolloError) => {
      handleErrorAlert(appolloError.message);
    },
  });
  const [moveRuleMutation] = useMutation(MOVE_RULE, {
    onError: (appolloError: ApolloError) => {
      handleErrorAlert(appolloError.message);
    },
    refetchQueries: [{ query: GET_RULES }],
  });
  const [updateRuleMutation] = useMutation(UPDATE_RULE, {
    onCompleted: () => {
      handleSuccessAlert('Filter saved');
      handleFormReset();
    },
    onError: (appolloError: ApolloError) => {
      handleErrorAlert(appolloError.message);
    },
  });
  const [deleteRuleMutation] = useMutation(DELETE_RULE, {
    onCompleted: () => {
      handleSuccessAlert('Filter deleted');
    },
    onError: (appolloError: ApolloError) => {
      handleErrorAlert(appolloError.message);
    },
  });

  function handleRuleAdd(rule: Partial<ScrubbingRule>): void {
    addRuleMutation({
      variables: {
        scrubbingRule: {
          ...rule,
        },
      },
      refetchQueries: [{ query: GET_RULES }],
    });
  }
  function handleRuleCopy(id: number): void {
    const values = pick(
      scrubbingRules.find((rule) => rule.id === id),
      ['name', 'logFilter', 'matchText', 'replaceWith'],
    ) as ScrubbingRule;
    const newRule = { ...values, name: `${values.name} copy` };
    handleRuleAdd(newRule);
    const filterFormData: ScrubbingRuleFormData = {
      variant: ScrubbingRuleFormVariant.UPDATE,
      values: newRule,
    };
    setScrubbingRuleForm(() => filterFormData);
    setFormValidated(false);
  }
  function handleRuleEdit(id: number): void {
    setScrubbingRuleForm(() => ({
      variant: ScrubbingRuleFormVariant.UPDATE,
      values: scrubbingRules.find((rule) => rule.id === id) as ScrubbingRule,
    }));
    setFormValidated(false);
    setFilterFade(true);
  }
  function handleRuleMove(id: number, position: number): void {
    moveRuleMutation({
      variables: {
        id,
        position,
      },
    });
  }
  function handleRuleUpdate(rule: Partial<ScrubbingRule>): void {
    updateRuleMutation({
      variables: {
        id: rule.id,
        scrubbingRule: {
          id: rule.id,
          logFilter: rule.logFilter,
          matchText: rule.matchText,
          name: rule.name,
          position: rule.position,
          replaceWith: rule.replaceWith,
        },
      },
      refetchQueries: [{ query: GET_RULES }],
    });
  }
  function handleRuleDelete(id: number): void {
    deleteRuleMutation({
      variables: {
        id,
      },
      refetchQueries: [{ query: GET_RULES }],
    });
  }
  function handleFormChange(event: ChangeEvent<HTMLInputElement>): void {
    event.persist();
    setScrubbingRuleForm((draft: ScrubbingRuleFormData) => {
      const { name, value } = event.target;
      // @ts-expect-error false trigger for TS7053, computed properties, TypeScript v3.7.2
      draft.values[name] = value;
    });
  }
  function handleFormReset(): void {
    setScrubbingRuleForm(() => scrubbingRuleFormInitialValue);
    setFormValidated(false);
  }
  function handleFormSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      setFormValidated(true);
      return;
    }

    switch (scrubbingRuleForm.variant) {
      case ScrubbingRuleFormVariant.ADD:
        handleRuleAdd(scrubbingRuleForm.values);
        break;
      case ScrubbingRuleFormVariant.UPDATE:
        handleRuleUpdate(scrubbingRuleForm.values);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn(`form variant "${scrubbingRuleForm.variant}" is not handled`);
    }
  }
  function handleListItemDrag(dragIndex: number, hoverIndex: number): void {
    const rules = JSON.parse(JSON.stringify(scrubbingRules));
    const scrubbingRule = rules[dragIndex];

    // remove dragged item
    rules.splice(dragIndex, 1);
    // insert dragged item
    rules.splice(hoverIndex, 0, scrubbingRule);

    // update 'position' property values
    for (let i = 0; i < rules.length; i += 1) {
      rules[i].position = i;
    }

    updateQuery(() => ({ scrubbingRules: rules }));
  }
  function handleListItemDrop(id: number, position: number): void {
    handleRuleMove(id, position);
  }
  function handleErrorAlert(message: string): void {
    setPageAlertConfig({
      message: clearGraphQLErrorMessage(message),
      variant: PageAlertVariant.ERROR,
      autoHide: false,
    });
  }
  function handleSuccessAlert(message: string): void {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
      duration: 1.5,
    });
  }

  const accountData = useRecoilValue(accountDataState);
  const { isSingularityXDRUser = false } = accountData;

  const hasFullAccessPermission = useRecoilValue(hasPermissionState(Permission.fullAccess));
  const hasXdrUserPermission = useRecoilValue(hasPermissionState(Permission.xdrUser));

  function hasRequiredPermissions(): boolean {
    return (!isSingularityXDRUser && hasFullAccessPermission) || (isSingularityXDRUser && hasXdrUserPermission);
  }

  function renderFormTitle(): JSX.Element | null {
    switch (scrubbingRuleForm.variant) {
      case ScrubbingRuleFormVariant.ADD:
        return <h2>New Filter</h2>;
      case ScrubbingRuleFormVariant.UPDATE:
        if (!hasRequiredPermissions()) {
          return <h2>View Filter</h2>;
        }
        return <h2>Edit Filter</h2>;
      default:
        return null;
    }
  }

  const isFormHidden = !hasRequiredPermissions() && scrubbingRuleForm.variant === ScrubbingRuleFormVariant.ADD;

  return (
    <DndProvider backend={HTML5Backend}>
      <Page docTitle="Log Processing" testId={ELEMENT.scrubbingPage}>
        <PageHeader title="Log Processing" helpLink={HELP_ROUTE.HELP_LOG_PROCESSING} />
        <div className="LogProcessingPage__Layout">
          <div className="row">
            <div className="LogProcessingPage__ScrubbingRulesList col-md">
              <h2>Scrubbing Filters</h2>
              <ScrubbingRulesList
                scrubbingRules={scrubbingRules}
                isWriteDisabled={!hasRequiredPermissions()}
                onListItemDrag={handleListItemDrag}
                onListItemDrop={handleListItemDrop}
                onRuleCopy={handleRuleCopy}
                onRuleEdit={handleRuleEdit}
                onRuleDelete={handleRuleDelete}
              />
            </div>
            <div
              className="LogProcessingPage__ScrubbingRuleForm col-md"
              style={{ display: isFormHidden ? 'none' : 'initial' }}
            >
              {renderFormTitle()}
              <div
                className={filterFade ? 'LogProcessingPage-fade' : ''}
                onAnimationEnd={(): void => setFilterFade(false)}
              >
                <ScrubbingRuleForm
                  data={scrubbingRuleForm}
                  isWriteDisabled={!hasRequiredPermissions()}
                  validated={formValidated}
                  saveDisabled={!hasRequiredPermissions()}
                  onSubmit={handleFormSubmit}
                  onChange={handleFormChange}
                  onReset={handleFormReset}
                  scrubbingfilterRules={scrubbingfilterRules}
                />
              </div>
            </div>
          </div>
          <div className="LogProcessingPage__AboutScrubbing">
            <h2>About Scrubbing</h2>
            <AboutScrubbing scrubbingfilterRules={scrubbingfilterRules} />
          </div>
        </div>
      </Page>
    </DndProvider>
  );
};

export default LogProcessingPage;
